<template>
  <div class="relative w-full flex-col items-center text-white bg-[#120F14] mt-[70px] pb-[80px]">
    <!-- Spinner while loading -->
    <div v-if="loading" class="flex justify-center items-center min-h-screen w-full">
      <div class="twist-spinner w-[60px] h-[60px]"></div>
    </div>

    <div v-else class="w-full h-full flex flex-col items-center justify-start">
      <!-- Header Container -->
      <div class="w-full max-w-[1400px] mx-auto pt-6 px-4 lg:px-8 xl:px-10">
        <!-- Sub Header Row -->
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between w-full mb-6 gap-4">
          <div class="flex flex-wrap items-center gap-4">
            <button @click="$router.push('/guess-the-balance')" class="flex items-center justify-center w-8 h-8 rounded bg-[#1A1D24] hover:bg-[#2A2E38] transition-colors border border-white/5">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
            </button>
            
            <div class="px-2 py-1 rounded-[4px] text-[10px] font-bold uppercase tracking-wider" 
                 :class="gtbDetails?.status === 'done' ? 'bg-[#5A7E22] text-[#B5E550]' : 'bg-[#00F0FF]/20 text-[#00F0FF]'">
              {{ gtbDetails?.status === 'done' ? $t('GuessTheBalanceDetails.CompletedEvent') : $t('GuessTheBalanceDetails.OngoingEvent') }}
            </div>
            
            <h2 class="text-xl md:text-2xl font-bold text-white uppercase font-sans m-0">
              {{ $t('GuessTheBalance.Header') }} <span class="text-[#00F0FF]">#{{ gtbDetails?.id?.substring(0, 4) || '...' }}</span>
            </h2>
          </div>
          
          <div class="flex items-center gap-2">
            <img src="@/assets/revamp/icons/calendar.png" class="w-4 h-4 object-contain" />
            <span class="text-sm font-bold text-gray-400">{{ animatedDate }}</span>
          </div>
        </div>

        <!-- Banner -->
        <div class="relative w-full min-h-[180px] sm:min-h-[200px] overflow-hidden rounded-[16px] flex flex-col md:flex-row items-center justify-between p-6 sm:p-8 xl:p-10 gap-6 mb-10">
          <div class="absolute inset-0 bg-[#14181F] border border-white/5 rounded-[16px] pointer-events-none z-0"></div>
          <div class="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent z-0 pointer-events-none"></div>
          
          <div class="relative z-10 flex flex-col">
            <h1 class="text-4xl lg:text-5xl font-black text-white tracking-wide uppercase font-sans m-0">
              {{ $t('GuessTheBalance.Header') }}
            </h1>
            <p class="text-sm lg:text-base text-gray-300 mt-2 font-medium max-w-2xl">
              {{ $t('GuessTheBalance.Description') }}
            </p>
          </div>
        </div>

        <!-- Session Countdown -->
        <div v-if="gtbDetails && gtbDetails.status !== 'ongoing' && countdownActive" class="w-full bg-[#14181F] rounded-xl border border-[#00F0FF]/20 p-6 flex flex-col items-center mb-10">
          <h2 class="text-xs font-bold text-[#00F0FF] uppercase tracking-widest mb-2">{{ $t('GuessTheBalanceDetails.SessionCountdown') }}</h2>
          <div class="text-4xl md:text-5xl font-black text-white tracking-widest font-sans">{{ countdownTime }}</div>
        </div>

        <!-- Stat Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <div class="bg-[#14181F] rounded-xl p-6 flex flex-col justify-center border border-white/5">
            <div class="flex items-center gap-2 mb-2">
              <img src="@/assets/revamp/icons/money.png" class="w-4 h-4 object-contain" />
              <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ $t('GuessTheBalanceDetails.StartingBalance') }}</span>
            </div>
            <div class="text-2xl md:text-3xl font-black text-white">$ {{ animatedStartingBalance }}</div>
          </div>

          <div class="bg-[#14181F] rounded-xl p-6 flex flex-col justify-center border border-white/5">
            <div class="flex items-center gap-2 mb-2">
              <img src="@/assets/revamp/icons/points.png" class="w-4 h-4 object-contain" />
              <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ $t('GuessTheBalanceDetails.EndingBalance') }}</span>
            </div>
            <div class="text-2xl md:text-3xl font-black text-white">$ {{ animatedEndingBalance }}</div>
          </div>
        </div>

        <!-- Winner Section -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-4xl mx-auto mb-12 mt-4">
          <!-- GTB Winner (Champion Theme) -->
          <div class="relative bg-[#1A1D24] rounded-2xl p-[1px] overflow-hidden group hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300 animate-glow-pulse z-10">
            <div class="absolute inset-0 bg-gradient-to-b from-[#00F0FF] to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div class="relative bg-[#14181F] rounded-[15px] p-6 flex flex-col items-center h-full">
              <div class="w-16 h-16 rounded-full bg-[#00F0FF] text-black flex items-center justify-center font-black text-3xl mb-4 group-hover:scale-110 transition-transform duration-300">
                1
              </div>
              <div class="text-[#00F0FF] text-[10px] font-bold tracking-widest uppercase mb-1">
                {{ $t("GuessTheBalanceDetails.gtbWinner") }}
              </div>
              <h3 class="text-xl font-bold text-white mb-4">
                {{ truncatedUsernameGTB }}
              </h3>
              <div class="bg-white/5 rounded-lg px-6 py-2 flex flex-col items-center border border-white/5 min-w-[120px]">
                <span class="text-2xl font-black text-[#00F0FF]">{{ animatedPrizeGTB || "TBD" }}</span>
              </div>
              <div class="mt-4 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                {{ $t('GuessTheBalanceDetails.Guess') }} {{ animatedGuessGTB || $t('GuessTheBalance.TBD') }}
              </div>
            </div>
          </div>

          <!-- GTHP Winner (2nd Place Theme) -->
          <div class="relative bg-[#1A1D24] rounded-2xl p-[1px] flex flex-col items-center h-full group hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300 animate-subtle-pulse z-10">
            <div class="absolute inset-0 bg-gradient-to-b from-[#BEFF2E] to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
            <div class="relative bg-[#14181F] rounded-[15px] p-6 flex flex-col items-center h-full w-full">
              <div class="w-16 h-16 rounded-full bg-[#2A2E38] text-white flex items-center justify-center font-black text-2xl mb-4 group-hover:scale-110 group-hover:bg-[#3A3F4C] transition-all duration-300">
                2
              </div>
              <div class="text-gray-400 text-[10px] font-bold tracking-widest uppercase mb-1 flex items-center gap-1">
                {{ $t("GuessTheBalanceDetails.gthpWinner") }}
              </div>
              <h3 class="text-xl font-bold text-white mb-4">
                {{ truncatedUsernameGTHP }}
              </h3>
              <div class="bg-white/5 rounded-lg px-6 py-2 flex flex-col items-center border border-white/5 min-w-[120px]">
                <span class="text-2xl font-black text-white">{{ animatedPrizeGTHP || "TBD" }}</span>
              </div>
              <div class="mt-4 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                {{ $t('GuessTheBalanceDetails.Guess') }} {{ animatedGuessGTHP || $t('GuessTheBalance.TBD') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- My Guess Summary and Add Guess Button -->
      <div class="w-full max-w-[1400px] mx-auto px-4 lg:px-8 xl:px-10 mb-6">
        <div class="flex flex-col lg:flex-row justify-between items-center gap-6 bg-[#14181F] rounded-xl p-6 border border-[#00F0FF]/20 shadow-[0_0_12px_#00F0FF20]">
          <div class="flex flex-col sm:flex-row items-center gap-6 text-white text-sm font-bold uppercase tracking-wider">
            <div class="flex items-center gap-2">
              <span class="text-gray-500">{{ $t("GuessTheBalanceDetails.myGuessData.username") }}:</span>
              <span class="text-[#00F0FF]">{{ userGuess?.user?.username || "-" }}</span>
            </div>
            <div class="hidden sm:block w-[1px] h-6 bg-white/10"></div>
            <div class="flex items-center gap-2">
              <span class="text-gray-500">{{ $t("GuessTheBalanceDetails.myGuessData.gtbGuess") }}:</span>
              <span class="text-white">{{ userGuess?.guess != null ? "$" + userGuess.guess : "-" }}</span>
            </div>
            <div class="hidden sm:block w-[1px] h-6 bg-white/10"></div>
            <div class="flex items-center gap-2">
              <span class="text-gray-500">{{ $t("GuessTheBalanceDetails.myGuessData.gthpGuess") }}:</span>
              <span class="text-white">{{ userGuess?.gthpGuess != null ? "$" + userGuess.gthpGuess : "-" }}</span>
            </div>
          </div>

          <div class="flex items-center gap-6">
            <div class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
              <span class="text-gray-500">{{ $t('GuessTheBalanceDetails.YourPoints') }}</span>
              <span class="text-[#00F0FF] text-lg">{{ points != null ? points.toLocaleString() : "-" }}</span>
            </div>
            <div class="relative">
              <button
                @click="openGuessModal"
                @mouseenter="showTooltip = true"
                @mouseleave="showTooltip = false"
                :disabled="!isEligibleToGuess"
                class="px-5 py-2.5 rounded font-black transition-all duration-200 uppercase tracking-widest text-xs bg-gradient-to-r from-[#00F0FF] to-[#19A5CC] text-black shadow-[0_0_10px_#00F0FF50] hover:shadow-[0_0_15px_#00F0FF80]"
                :class="{ 'opacity-50 cursor-not-allowed grayscale': !isEligibleToGuess }"
              >
                + {{ $t("GuessTheBalanceDetails.Table.addGuessFor") }}
                <span v-if="gtbDetails?.requiredPoints">
                  {{ gtbDetails.requiredPoints }}
                  {{ gtbDetails.requiredPoints === 1 ? $t('GuessTheBalance.Point') : $t('GuessTheBalance.Points') }}
                </span>
              </button>
              
              <!-- Tooltip -->
              <transition name="fade">
                <div v-if="showTooltip && !isEligibleToGuess" class="absolute bg-[#1A1D24] border border-red-500/20 text-white text-xs p-3 rounded shadow-xl w-64 bottom-full right-0 mb-3 z-50 text-left">
                  <ul class="list-none p-0 m-0 space-y-2">
                    <li v-if="!isLoggedIn" class="flex items-center gap-2"><span class="text-red-500">❌</span><span>{{ $t("GuessTheBalanceDetails.TooltipMessages.needLogin") }}</span></li>
                    <li v-if="hasGuessed" class="flex items-center gap-2"><span class="text-red-500">❌</span><span>{{ $t("GuessTheBalanceDetails.TooltipMessages.alreadyGuessed") }}</span></li>
                    <li v-if="gtbDetails?.status === 'done'" class="flex items-center gap-2"><span class="text-red-500">❌</span><span>{{ $t("GuessTheBalanceDetails.TooltipMessages.sessionEnded") }}</span></li>
                    <li v-if="gtbDetails?.status === 'ongoing'" class="flex items-center gap-2"><span class="text-red-500">❌</span><span>{{ $t("GuessTheBalanceDetails.TooltipMessages.guessingClosed") }}</span></li>
                    <li v-if="isLoggedIn && !kick" class="flex items-center gap-2"><span class="text-red-500">❌</span><span>{{ $t("GuessTheBalanceDetails.TooltipMessages.missingKick") }}</span></li>
                    <li v-if="isLoggedIn && !stake" class="flex items-center gap-2"><span class="text-red-500">❌</span><span>{{ $t("GuessTheBalanceDetails.TooltipMessages.missingStake") }}</span></li>
                  </ul>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>

      <!-- Entries Section -->
      <div class="w-full max-w-[1400px] mx-auto px-4 lg:px-8 xl:px-10">
        <div class="w-full bg-[#14181F] rounded-xl border border-white/5 p-4 sm:p-6 mb-6">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-lg md:text-xl font-bold text-white uppercase font-sans flex items-center">
              <img src="@/assets/revamp/icons/ticket.png" class="w-5 h-5 mr-3 object-contain" /> {{ $t('GuessTheBalanceDetails.BonusHistory') }}
            </h2>
            <div class="px-3 py-1 rounded bg-[#00F0FF]/10 text-[#00F0FF] text-[10px] font-bold uppercase tracking-wider">
              {{ totalItems }} {{ $t('GuessTheBalanceDetails.Bonuses') }}
            </div>
          </div>

          <div class="w-full overflow-x-auto">
            <div class="min-w-[1000px] flex flex-col">
              <!-- Table Header -->
              <div class="grid grid-cols-[2fr_1fr_1.5fr_2fr_2fr] gap-4 px-4 py-4 text-[10px] font-bold uppercase text-gray-500 border-b border-white/5 tracking-wider">
                <div class="text-left">{{ $t("GuessTheBalanceDetails.Table.slotName") }}</div>
                <div class="text-left">{{ $t("GuessTheBalanceDetails.Table.status") }}</div>
                <div class="text-right">{{ $t("GuessTheBalanceDetails.Table.bonusBuyCost") }}</div>
                <div class="text-right">{{ $t("GuessTheBalanceDetails.Table.payout") }}</div>
                <div class="text-right">{{ $t("GuessTheBalanceDetails.Table.profitLoss") }}</div>
              </div>

              <!-- Table Rows -->
              <div
                v-for="slot in paginatedSlots"
                :key="slot._id"
                class="grid grid-cols-[2fr_1fr_1.5fr_2fr_2fr] gap-4 px-4 py-4 items-center border-b border-white/5 hover:bg-white/5 transition-colors"
              >
                <div class="text-left font-bold text-sm text-white truncate max-w-[180px] flex items-center gap-3">
                  <img src="@/assets/revamp/icons/token.png" class="w-5 h-5 object-contain opacity-70" />
                  <span :title="slot.slotTitle">{{ slot.slotTitle }}</span>
                </div>
                <div class="text-left uppercase text-[10px] font-bold text-gray-400 tracking-widest">{{ slot.status }}</div>
                <div class="text-right text-xs font-bold text-gray-300">
                  {{ slot.bonusBuyCost != null ? `$${slot.bonusBuyCost.toFixed(2)}` : "$0.00" }}
                </div>
                <div class="text-right text-xs font-bold text-[#00F0FF]">
                  {{ slot.bonusBuyPayout != null ? `$${slot.bonusBuyPayout.toFixed(2)}` : "$0.00" }}
                </div>
                <div
                  class="text-right text-xs font-bold"
                  :class="{
                    'text-green-400': slot.profit > 0,
                    'text-red-400': slot.profit < 0,
                    'text-gray-400': slot.profit === 0 || slot.profit == null,
                  }"
                >
                  {{ slot.profit != null ? `${slot.profit < 0 ? "-$" : "$"}${Math.abs(slot.profit).toFixed(2)}` : "$0.00" }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="w-full mt-6">
          <div class="hidden lg:block w-full">
            <div class="p-[1px] bg-gradient-to-r from-[#00F0FF] to-[#A144F6] rounded-[10px]">
              <div class="bg-[#14181F] rounded-[9px] flex justify-between items-center w-full px-6 h-[70px]">
                <p class="text-white font-bold text-sm sm:text-base font-sans">
                  {{ $t('Store.Showing') }} {{ itemStart }} - {{ itemEnd }} {{ $t('Store.Of') }} {{ totalItems }} {{ $t('Store.Items') }}
                </p>
                
                <div class="flex items-center gap-2">
                  <button @click="prevPage" :disabled="currentPage === 1" class="flex items-center justify-center w-10 h-10 rounded-[8px] bg-[#0D0B10] border border-[#00F0FF]/20 text-white disabled:opacity-50 hover:bg-white/5 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                  </button>

                  <span v-for="page in totalPages" :key="page">
                    <button @click="goToPage(page)" :class="['w-10 h-10 rounded-[8px] font-bold transition-colors text-sm', currentPage === page ? 'bg-[#00F0FF] text-black border border-[#00F0FF]' : 'bg-[#0D0B10] border border-[#00F0FF]/20 text-white hover:bg-white/5']">
                      {{ page }}
                    </button>
                  </span>

                  <button @click="nextPage" :disabled="currentPage === totalPages" class="flex items-center justify-center w-10 h-10 rounded-[8px] bg-[#0D0B10] border border-[#00F0FF]/20 text-white disabled:opacity-50 hover:bg-white/5 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-col items-center mt-6 lg:hidden w-full">
            <div class="w-full p-[1px] bg-gradient-to-r from-[#00F0FF] to-[#A144F6] rounded-[10px]">
              <div class="bg-[#14181F] rounded-[9px] flex flex-col items-center w-full py-6 px-4">
                <p class="text-white font-bold text-lg mb-4 text-center">
                  {{ $t('Store.Showing') }} {{ itemStart }} - {{ itemEnd }} {{ $t('Store.Of') }} {{ totalItems }} {{ $t('Store.Items') }}
                </p>
                <div class="flex flex-wrap justify-center gap-2">
                  <button @click="prevPage" :disabled="currentPage === 1" class="flex items-center justify-center w-12 h-12 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                  </button>

                  <span v-for="page in totalPages" :key="page">
                    <button @click="goToPage(page)" :class="['w-12 h-12 rounded-md font-bold transition-all border', currentPage === page ? 'bg-[#0D0B10] border-[#00F0FF] text-[#00F0FF]' : 'bg-[#0D0B10] border-transparent text-gray-500 hover:text-white']">
                      {{ page }}
                    </button>
                  </span>

                  <button @click="nextPage" :disabled="currentPage === totalPages" class="flex items-center justify-center w-12 h-12 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <AddGuessModal
    v-if="showAddGuessModal"
    :sessionId="gtbDetails?.id"
    :requiredPoints="gtbDetails?.requiredPoints"
    @guessSubmitted="handleGuessSubmitted"
    @close="showAddGuessModal = false"
  />
</template>

<script>
import axios from "axios";
import moment from "moment-timezone";
import AddGuessModal from "@/components/AddGuessModal.vue";

export default {
  name: "GuessTheBalanceDetailsPage",
  components: { AddGuessModal },
  data() {
    return {
      gtbDetails: null,
      gtbSlots: [],
      userGuess: null,

      // UI State
      loading: false,
      errorMessage: "",
      countdownTime: "00:00",
      countdownActive: true,
      hasGuessed: false,

      // Modals
      showAddGuessModal: false,

      // Pagination
      currentPage: 1,
      itemsPerPage: 10,

      // Animations
      animatedStartingBalance: 0,
      animatedEndingBalance: 0,
      animatedUsernameGTB: "",
      animatedUsernameGTHP: "",
      animatedGuessGTB: 0,
      animatedGuessGTHP: 0,
      animatedPrizeGTB: 0,
      animatedPrizeGTHP: 0,
      animatedPrizeGTBMobile: 0,
      animatedPrizeGTHPMobile: 0,
      animatedDate: "",
      dateAnimationInterval: null,

      // User Account
      points: "",
      userId: "",
      isLoggedIn: false,
      stake: "",
      kick: "",

      showTooltip: false,
    };
  },
  computed: {
    isEligibleToGuess() {
      return (
        this.isLoggedIn &&
        !this.hasGuessed &&
        this.gtbDetails?.status === "started" &&
        this.kick &&
        this.stake
      );
    },
    truncatedUsernameGTB() {
      const name = this.animatedUsernameGTB || "TBD";
      return name.length > 12 ? name.slice(0, 12) + "..." : name;
    },
    truncatedUsernameGTHP() {
      const name = this.animatedUsernameGTHP || "TBD";
      return name.length > 12 ? name.slice(0, 12) + "..." : name;
    },
    totalItems() {
      return this.gtbSlots.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    itemStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    itemEnd() {
      return Math.min(this.itemStart + this.itemsPerPage - 1, this.totalItems);
    },
    paginatedSlots() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.gtbSlots.slice(start, start + this.itemsPerPage);
    },
  },
  watch: {
    gtbDetails: {
      handler() {
        this.initializeTimer();
      },
      immediate: true,
    },
  },
  methods: {
    async fetchGTBDetails() {
      const gtbId = this.$route.params.id;
      if (!gtbId) return console.error("No GTB ID found in the URL.");
      this.loading = true;

      try {
        const {
          data,
        } = await axios.get(
          `${process.env.VUE_APP_BASE_API}/api/guess-the-balance/${gtbId}`,
          { headers: { "x-api-key": process.env.VUE_APP_X_API_KEY } }
        );

        const gtb = data.gtb || {};
        const bonus = gtb.bonusHuntId || {};
        const winnerUser = gtb?.winner?.user || {};
        const gthpUser = gtb?.gthpWinner?.user || {};

        this.gtbDetails = {
          id: gtb._id,
          prize: gtb.prize,
          requiredPoints: gtb.requiredPoints,
          status: gtb.status,
          timerStarted: gtb.timerTimeStarted,
          duration: gtb.timerDuration,
          createdAt: gtb.createdAt,
          startingBalance: bonus.infoStartCost || 0,
          endingBalance: bonus.infoAmountWon || 0,
          slotCount: data.slots?.length || 0,
          date: gtb.createdAt,
          winner: {
            username: winnerUser.username || "TBD",
            guess: gtb?.winner?.guess || 0,
            image: winnerUser.profileImage || require("@/assets/avatar-1.png"),
          },
          gthpWinner: {
            username: gthpUser.username || "TBD",
            guess: gtb?.gthpWinner?.guess || 0,
            image: gthpUser.profileImage || require("@/assets/avatar-1.png"),
          },
          gthpPrize: gtb?.gthpPrize ?? 0,
        };

        this.gtbSlots = Array.isArray(data.slots) ? data.slots : [];

        this.animateValues();
      } catch (error) {
        console.error("Error fetching GTB details:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchUserDetails() {
      const token = localStorage.getItem("maherco-access-token");

      if (token) {
        this.loading = true;
        this.$store.commit("UPDATE_USER", { accessToken: token });

        try {
          const res = await axios.get(process.env.VUE_APP_BASE_API + "/api/users/me", {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
              Authorization: `Bearer ${token}`,
            },
          });

          if (res.data) {
            this.stake = res.data.user.stake;
            this.kick = res.data.user.kick;
            this.isVerified = res.data.user.isVerified;
            this.userId = res.data.user._id;

            // ✅ Call fetchUserGuesses here after userId is set
            await this.fetchUserGuesses();
          }
        } catch (error) {
          if (error.response?.status === 401) {
            localStorage.removeItem("vuex");
            localStorage.removeItem("accessToken");
            window.location.reload();
          } else {
            console.log(`Error: ${error.response.status}`);
          }
        } finally {
          this.loading = false;
        }
      }
    },
    async fetchUserGuesses() {
      const gtbId = this.$route.params.id;
      const token = localStorage.getItem("maherco-access-token");
      const myUserId = this.userId;

      if (!gtbId || !token || !myUserId) {
        console.warn("Missing GTB ID, token, or user ID.");
        return;
      }

      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_BASE_API}/api/guess-the-balance/guess/${gtbId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        if (data && Array.isArray(data.gtbGuesses)) {
          const userGuess = data.gtbGuesses.find(
            (guess) => guess.user && guess.user._id === myUserId
          );
          if (userGuess) {
            this.hasGuessed = true;
            this.userGuess = userGuess;
          }
        }
      } catch (error) {
        console.error("Error fetching user guesses:", error);
        if (error.response?.status === 401) {
          this.errorMessage = "Session expired. Please log in again.";
        }
      }
    },

    async fetchUserPoints() {
      const token = localStorage.getItem("maherco-access-token");
      if (!token) return this.showError("You must be logged in to view your points.");

      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_BASE_API}/api/users/me/points`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        this.points = data.points;
      } catch (e) {
        console.error("Point fetch failed:", e);
      }
    },

    animateValues() {
      const d = this.gtbDetails;

      console.log(d, "test");

      this.animateNumber("animatedStartingBalance", d.startingBalance);
      this.animateNumber("animatedEndingBalance", d.endingBalance);

      // GTB animations
      this.animateName("animatedUsernameGTB", d.winner?.username ?? "");
      this.animateNumber("animatedGuessGTB", d.winner?.guess ?? 0);
      this.animateNumber("animatedPrizeGTB", d.prize ?? 0);

      // GTHP animations
      this.animateName("animatedUsernameGTHP", d.gthpWinner?.username ?? "");
      this.animateNumber("animatedGuessGTHP", d.gthpWinner?.gthpGuess ?? 0);
      this.animateNumber("animatedPrizeGTHP", d.gthpPrize ?? 0);

      this.animateDate(d.date);
    },

    animateNumber(variable, targetValue) {
      const isCurrency = [
        "animatedGuessGTB",
        "animatedGuessGTHP",
        "animatedPrizeGTB",
        "animatedPrizeGTHP",
        "animatedPrizeGTBMobile",
        "animatedPrizeGTHPMobile",
      ].includes(variable);

      const duration = 2000; // animation duration in ms
      const frameRate = 60;
      const frameTime = 1000 / frameRate;
      const totalFrames = Math.round(duration / frameTime);

      let startValue = 0; // Always start from 0
      targetValue = Number(targetValue);
      const valueDelta = targetValue;

      let currentFrame = 0;

      const interval = setInterval(() => {
        currentFrame++;
        const progress = currentFrame / totalFrames;
        const easedProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic

        const currentValue = startValue + valueDelta * easedProgress;

        if (progress >= 1) {
          clearInterval(interval);
          this[variable] = isCurrency
            ? `$${targetValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
            : targetValue.toLocaleString();
        } else {
          const roundedValue = Math.floor(currentValue);
          this[variable] = isCurrency
            ? `$${roundedValue.toLocaleString()}`
            : roundedValue.toLocaleString();
        }
      }, frameTime);
    },
    animateDate(targetDate) {
      if (!targetDate) return;

      let currentDate = new Date(new Date().getFullYear(), 0, 1);
      const finalDate = new Date(targetDate);

      this.dateAnimationInterval = setInterval(() => {
        if (currentDate >= finalDate) {
          clearInterval(this.dateAnimationInterval);
          this.animatedDate = finalDate.toLocaleDateString("en-US");
        } else {
          currentDate.setDate(currentDate.getDate() + 2);
          this.animatedDate = currentDate.toLocaleDateString("en-US");
        }
      }, 30);
    },

    animateName(targetKey, realUsername) {
      if (!realUsername) return;

      let counter = 0;
      const usernameLength = realUsername.length;
      const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

      const generateRandomString = (length) => {
        return Array.from(
          { length },
          () => characters[Math.floor(Math.random() * characters.length)]
        ).join("");
      };

      const interval = setInterval(() => {
        if (counter >= 10) {
          clearInterval(interval);
          this[targetKey] = realUsername;
        } else {
          this[targetKey] = generateRandomString(usernameLength);
          counter++;
        }
      }, 100);
    },

    initializeTimer() {
      const { timerStarted, duration } = this.gtbDetails || {};
      if (!timerStarted || !duration) return;

      const endTime = moment.utc(timerStarted).add(duration, "minutes").valueOf();

      if (this.countdownInterval) clearInterval(this.countdownInterval);

      this.countdownInterval = setInterval(() => {
        const now = moment().utc().valueOf();
        const remaining = endTime - now;

        if (remaining <= 0) {
          clearInterval(this.countdownInterval);
          this.countdownTime = "00:00";
          this.countdownActive = false;
        } else {
          const minutes = Math.floor(remaining / 60000);
          const seconds = Math.floor((remaining % 60000) / 1000);
          this.countdownTime = `${String(minutes).padStart(2, "0")}:${String(
            seconds
          ).padStart(2, "0")}`;
        }
      }, 1000);
    },

    checkLoginStatus() {
      this.isLoggedIn = !!localStorage.getItem("maherco-access-token");
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    },

    openGuessModal() {
      if (!this.isEligibleToGuess) return;
      this.showAddGuessModal = true;
    },

    // Pagination Controls
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    goToPage(page) {
      this.currentPage = page;
    },
    handleGuessSubmitted() {
      window.location.reload();
    },
  },
  async mounted() {
    await this.fetchGTBDetails();
    await this.fetchUserDetails();
    await this.fetchUserPoints();
    this.checkLoginStatus();
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}
.fade-leave-from {
  opacity: 1;
}
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

@keyframes scroll-dot {
  0% {
    transform: translate(20%, -50%); /* Start exactly at left */
    opacity: 0.8;
  }
  50% {
    transform: translate(270%, -50%); /* Move to far right */
    opacity: 1;
  }
  100% {
    transform: translate(20%, -50%);
    opacity: 0.8;
  }
}

.animate-scroll-dot {
  animation: scroll-dot 5s infinite ease-in-out;
}

@keyframes glow-pulse {
  0%, 100% {
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.2);
  }
  50% {
    box-shadow: 0 0 40px rgba(0, 240, 255, 0.5);
  }
}

@keyframes subtle-pulse {
  0%, 100% {
    box-shadow: 0 0 15px rgba(190, 255, 46, 0.2);
  }
  50% {
    box-shadow: 0 0 40px rgba(190, 255, 46, 0.5);
  }
}

.animate-glow-pulse {
  animation: glow-pulse 2.5s ease-in-out infinite;
}

.animate-subtle-pulse {
  animation: subtle-pulse 3s ease-in-out infinite;
}
</style>
